import clsx from "clsx";
import { ReactNode } from "react";
import { GridRowCellLoadingIndicator } from "../../components";
import styles from "./difference-matrix.module.scss";
import { EMPTY_VALUE } from "./consts";
import {
  cellDiffers,
  MATRIX_COLUMNS,
  MatrixCell,
  MatrixColumnName,
  MatrixValues,
  matrixCell,
  referenceCellText
} from "./differenceMatrixFields";

const COLUMN_WIDTHS: Record<MatrixColumnName, string> = {
  file_type: "8%",
  file_size: "6%",
  extracted_files: "4%",
  threat_name: "10%",
  av_detections: "9%",
  cert_validation: "8%",
  system_tags: "8%",
  capabilities: "6%"
};

const SAME = "=";

interface MatrixColumnFieldsProps<TRow> {
  reference: MatrixValues | undefined;
  valuesFor: (sample: TRow) => MatrixValues;
  hideSame?: boolean;
  /** Per grid, since each has its own leading columns to fit beside these. */
  widths?: Partial<Record<MatrixColumnName, string>>;
}

interface RenderedCell {
  className?: string;
  /** These cells truncate, and the hover is where the whole value is. */
  title?: string;
  content: ReactNode;
}

const setTitle = (cell: Extract<MatrixCell, { kind: "set" }>): string =>
  [
    cell.gained.length ? `Gained: ${cell.gained.join(", ")}` : "",
    cell.lost.length ? `Lost: ${cell.lost.join(", ")}` : "",
    `${cell.shared} shared`
  ]
    .filter(Boolean)
    .join("\n");

const renderCell = (cell: MatrixCell, hideSame: boolean): RenderedCell => {
  switch (cell.kind) {
    case "unavailable":
      return { content: EMPTY_VALUE };
    case "scalar":
      if (!cell.differs) {
        return {
          title: cell.text,
          content: hideSame ? (
            SAME
          ) : (
            <span className={styles.value}>{cell.text}</span>
          )
        };
      }
      return {
        className: styles["cell--differs"],
        title: `${cell.text} (differs from the main sample)`,
        content: (
          <>
            <span className={styles.value}>{cell.text}</span>
            {cell.delta && <span className={styles.delta}>{cell.delta}</span>}
          </>
        )
      };
    case "set":
      if (!cellDiffers(cell)) {
        return {
          title: setTitle(cell),
          content: hideSame ? (
            SAME
          ) : (
            <span className={styles.value}>{cell.count}</span>
          )
        };
      }
      return {
        className: styles["cell--differs"],
        title: setTitle(cell),
        content: (
          <>
            <span className={styles.value}>{cell.count}</span>
            <span className={clsx(styles.delta, styles["set-counts"])}>
              {cell.gained.length > 0 && (
                <span className={styles.gained}>+{cell.gained.length}</span>
              )}
              {cell.lost.length > 0 && (
                <span className={styles.lost}>−{cell.lost.length}</span>
              )}
            </span>
          </>
        )
      };
    default: {
      const unhandled: never = cell;
      throw new Error(`Unhandled matrix cell: ${JSON.stringify(unhandled)}`);
    }
  }
};

export interface MainSample {
  sha1: string;
  fileName?: string;
}

export const mainSampleName = ({ sha1, fileName }: MainSample) => (
  <span className={styles["main-sample"]}>
    <span className={styles["main-sample__badge"]}>Main</span>
    <span className={clsx(styles.value, styles["pinned-value"])} title={sha1}>
      {fileName || sha1}
    </span>
  </span>
);

const matrixColumnFields = <TRow extends { sha1: string; loading?: boolean }>({
  reference,
  valuesFor,
  hideSame = false,
  widths = {}
}: MatrixColumnFieldsProps<TRow>) =>
  MATRIX_COLUMNS.map(column => ({
    name: `matrix_${column.name}`,
    displayTitle: (
      <span className="bold" title={column.title}>
        {column.shortTitle}
      </span>
    ),
    sortable: false,
    component: (sample: TRow) => {
      // Only the RHA grid seeds rows before their data arrives.
      if (sample.loading) return <GridRowCellLoadingIndicator />;
      const cell = matrixCell(column.name, valuesFor(sample), reference);
      const { className, title, content } = renderCell(cell, hideSame);
      return (
        <span
          className={clsx(styles.cell, className)}
          data-testid={`difference-matrix-${column.name}-${sample.sha1}`}
          title={title}
        >
          {content}
          {cellDiffers(cell) && (
            <span className="sr-only"> differs from the main sample</span>
          )}
        </span>
      );
    },
    pinnedComponent: () => (
      <span className={clsx(styles.cell, styles["pinned-value"])}>
        <span className={styles.value}>
          {referenceCellText(column.name, reference)}
        </span>
      </span>
    ),
    className: "overflow-ellipsis text-center",
    style: { width: widths[column.name] ?? COLUMN_WIDTHS[column.name] }
  }));

export default matrixColumnFields;
